import type { AnyAction } from '@reduxjs/toolkit';
import * as appActions from 'Actions/app';
import * as repertoireActions from 'Actions/repertoire';
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import { call, debounce, put, select, takeEvery } from 'redux-saga/effects';
import { actionTypes } from 'Types';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';
import Logger from 'Utils/Logger';
import {
  accountDefinitions,
  fromAccountSettingsPayload,
  isAccountCoordinate,
  toAccountSettingsPayload,
  type AccountSettingsPayload,
} from './accountSettings';
import { currentDeviceType } from './deviceType';
import { builtInDefault } from './resolveSetting';
import { bindingFor } from './selectSetting';
import {
  accountSettingsConfirmed,
  accountSettingsLoaded,
  accountSettingsQueued,
  layerCoordinate,
  selectPendingSettingWrites,
  selectSettingLayerValue,
  selectSyncedSettingValue,
  settingLayerValueSet,
  type SettingCoordinate,
  type SettingCoordinateValue,
  type SetSettingLayerValuePayload,
} from './settingsLayersSlice';
import type { PlayerLayer, SettingDefinition, SettingsState } from './settingTypes';

/**
 * Settings that follow the account (`USE_ACCOUNT_SETTINGS`).
 *
 * - At sign-in the account's values are loaded from `/user-settings`. A value
 *   with a store of its own on the device (a binding: finger numbers in
 *   mainAppReducer, a tier's tempo in tempoValues) is put there; the rest go
 *   to `settingsLayers`. A change made on this device and not yet sent keeps
 *   the newer local value.
 * - Each player change, whether a screen dispatched its usual action or wrote
 *   through the model, is queued and sent shortly after the last change.
 *   Today's screens are unchanged: the sync watches the actions they dispatch.
 * - This device keeps a copy of every value (the bound stores, and
 *   `settingsLayers`, both redux-persisted), so the app works offline; queued
 *   changes are sent at the next change or sign-in.
 * - Without the endpoint (a 404 from an older backend), nothing is sent and
 *   the app runs on this device's values, as before.
 */

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
export const ACCOUNT_SETTINGS_URL = `${baseUrl}user-settings`;

/** Wait this long after the last change before saving (a dragged slider sends once). */
export const ACCOUNT_SETTINGS_SAVE_DELAY_MS = 800;

const HYDRATION = 'accountSettingsHydration';

/** An action that puts a loaded value into a store: not a player change, so never sent back. */
export const asAccountHydration = (action: AnyAction): AnyAction => ({
  ...action,
  meta: { ...(action.meta ?? {}), [HYDRATION]: true },
});

const isAccountHydration = (action: AnyAction): boolean =>
  Boolean((action.meta as Record<string, unknown> | undefined)?.[HYDRATION]);

const isOn = (): boolean => isFeatureEnabled(FeatureFlag.USE_ACCOUNT_SETTINGS);

// False once the backend answered that it has no account store, until the next sign-in.
let accountStoreAvailable = true;

const isNotFound = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

// ---------------------------------------------------------------------------
// Which actions write which account setting
// ---------------------------------------------------------------------------

/** One existing action that writes a setting the account keeps, and how to place its value. */
export interface WatchedSettingAction {
  definition: SettingDefinition<unknown>;
  actionType: string;
  coordinateOf(action: AnyAction): SettingCoordinate | undefined;
}

const deviceTypeOf = (definition: SettingDefinition<unknown>) =>
  definition.residence === 'device_type' ? { deviceType: currentDeviceType() } : {};

const watchedActionsOf = (definition: SettingDefinition<unknown>): WatchedSettingAction[] => {
  const bindings = definition.bindings;
  const global = bindings?.global
    ? [
        {
          definition,
          actionType: bindings.global.write(definition.defaultValue).type,
          coordinateOf: () => layerCoordinate(definition.key, 'global', deviceTypeOf(definition)),
        },
      ]
    : [];
  const modes = Object.entries(bindings?.mode ?? {}).map(([mode, binding]) => ({
    definition,
    actionType: binding.write(definition.defaultValue).type,
    coordinateOf: () =>
      layerCoordinate(definition.key, 'mode', {
        mode: mode as SettingCoordinate['mode'],
        ...deviceTypeOf(definition),
      }),
  }));
  const instances = Object.entries(bindings?.instance ?? {}).map(([mode, binding]) => ({
    definition,
    actionType: binding.actionType,
    coordinateOf: (action: AnyAction) => {
      const instanceId = binding.instanceIdOf(action);
      return instanceId === undefined
        ? undefined
        : layerCoordinate(definition.key, 'instance', {
            mode: mode as SettingCoordinate['mode'],
            instanceId,
          });
    },
  }));
  // A layer the account does not keep (sight reading's old transposition column)
  // is watched too, and left out when its change is queued (isAccountCoordinate).
  return [...global, ...modes, ...instances];
};

/** Every existing action the sync watches, from the registry. */
export const watchedSettingActions = (): WatchedSettingAction[] =>
  accountDefinitions().flatMap(watchedActionsOf);

// ---------------------------------------------------------------------------
// Reading a layer's current value
// ---------------------------------------------------------------------------

const contextOf = (coordinate: SettingCoordinate) => ({
  ...(coordinate.mode ? { mode: coordinate.mode } : {}),
  ...(coordinate.instanceId ? { instanceId: coordinate.instanceId } : {}),
  ...(coordinate.deviceType ? { deviceType: coordinate.deviceType } : {}),
});

/** The value held at a coordinate on this device: its bound store, or settingsLayers. */
export const deviceValueAt = (
  state: SettingsState,
  definition: SettingDefinition<unknown>,
  coordinate: SettingCoordinate,
): unknown => {
  const binding = bindingFor(definition, coordinate.layer, contextOf(coordinate));
  return binding ? binding.select(state) : selectSettingLayerValue(state, coordinate);
};

/**
 * Whether a change at a coordinate needs sending: it differs from what the
 * account last confirmed, or another change is already waiting. A value the
 * account has never held that equals the built-in default is not sent (a
 * screen resetting to the default on open is not the player's choice).
 */
export const needsSending = (
  state: SettingsState,
  definition: SettingDefinition<unknown>,
  coordinate: SettingCoordinate,
  value: unknown,
): boolean => {
  if (!isAccountCoordinate(definition, coordinate)) return false;
  const pending = selectPendingSettingWrites(state).some(
    (write) => write.coordinate.key === coordinate.key && isEqual(write.coordinate, coordinate),
  );
  if (pending) return true;
  const synced = selectSyncedSettingValue(state, coordinate);
  if (synced === undefined) {
    return !isEqual(definition.parse(value), builtInDefault(definition, coordinate.deviceType));
  }
  return !isEqual(definition.parse(value), definition.parse(synced));
};

// ---------------------------------------------------------------------------
// The one global cursor: its old per-mode stores are copies the screens read
// ---------------------------------------------------------------------------

const CURSOR_DEVICE_KEY = 'museflow_cursor_visible';

function* copyCursorToOldStores(visible: boolean): Generator<unknown, void, unknown> {
  yield put(asAccountHydration(appActions.setSightReadingCursorVisible(visible)));
  yield put(asAccountHydration(repertoireActions.setCursorVisible(visible)));
  try {
    localStorage.setItem(CURSOR_DEVICE_KEY, String(visible));
  } catch {
    // Storage may be unavailable; the Redux copies still hold the value.
  }
}

// Sight reading's cursor toggle writes its old store; with the flag on it is
// the global cursor.
function* onSightReadingCursorToggled(action: AnyAction): Generator<unknown, void, unknown> {
  if (!isOn() || isAccountHydration(action)) return;
  yield put(
    settingLayerValueSet({ key: 'cursor', layer: 'global', context: {}, value: action.payload }),
  );
}

// ---------------------------------------------------------------------------
// Loading at sign-in
// ---------------------------------------------------------------------------

/**
 * Loads the account's values and puts each where this device keeps it. Never
 * throws: a failed load leaves this device's values in force.
 */
export function* loadAccountSettings(): Generator<unknown, void, unknown> {
  if (!isOn()) return;
  accountStoreAvailable = true;
  try {
    const response = (yield call(axios.get, ACCOUNT_SETTINGS_URL)) as {
      data?: AccountSettingsPayload;
    };
    const entries = fromAccountSettingsPayload(response?.data);
    const state = (yield select()) as SettingsState;
    const pending = selectPendingSettingWrites(state);
    const isPending = (coordinate: SettingCoordinate) =>
      pending.some((write) => isEqual(write.coordinate, coordinate));
    const loaded = entries.map((entry) => ({
      ...entry,
      binding: bindingFor(entry.definition, entry.coordinate.layer, contextOf(entry.coordinate)),
    }));
    // The account's values first, so the bound stores' writes below are not sent back.
    yield put(
      accountSettingsLoaded({
        values: loaded.map(({ coordinate, value, binding }) => ({
          coordinate,
          value,
          bound: binding !== undefined,
        })),
      }),
    );
    const hydrations = loaded
      .filter(({ coordinate }) => !isPending(coordinate))
      .map(({ binding, value }) => (binding ? binding.write(value) : undefined))
      .filter((write): write is AnyAction => write !== undefined);
    for (const write of hydrations) {
      yield put(asAccountHydration(write));
    }
    const cursor = loaded.find(
      ({ coordinate }) => coordinate.key === 'cursor' && coordinate.layer === 'global',
    );
    if (cursor && !isPending(cursor.coordinate)) {
      yield* copyCursorToOldStores(Boolean(cursor.value));
    }
    yield* saveAccountSettings();
  } catch (error) {
    if (isNotFound(error)) {
      accountStoreAvailable = false;
      Logger.info('Account settings: this backend has no /user-settings; using this device');
      return;
    }
    Logger.warn('Account settings: load failed; using this device', error);
  }
}

// ---------------------------------------------------------------------------
// Sending player changes
// ---------------------------------------------------------------------------

function* queueIfNeeded(
  definition: SettingDefinition<unknown>,
  coordinate: SettingCoordinate,
): Generator<unknown, void, unknown> {
  const state = (yield select()) as SettingsState;
  const value = deviceValueAt(state, definition, coordinate);
  if (value === undefined || !needsSending(state, definition, coordinate, value)) return;
  yield put(accountSettingsQueued({ writes: [{ coordinate, value: definition.parse(value) }] }));
}

function* onBoundSettingAction(action: AnyAction): Generator<unknown, void, unknown> {
  if (!isOn() || isAccountHydration(action)) return;
  const watched = watchedSettingActions().filter(({ actionType }) => actionType === action.type);
  for (const { definition, coordinateOf } of watched) {
    const coordinate = coordinateOf(action);
    if (coordinate) yield* queueIfNeeded(definition, coordinate);
  }
}

function* onSettingLayerValueSet(
  action: AnyAction & { payload: SetSettingLayerValuePayload },
): Generator<unknown, void, unknown> {
  if (!isOn() || isAccountHydration(action)) return;
  const { key, layer, context } = action.payload;
  const definition = accountDefinitions().find((candidate) => candidate.key === key);
  if (!definition) return;
  const coordinate = layerCoordinate(key, layer as PlayerLayer, context);
  yield* queueIfNeeded(definition, coordinate);
  if (key === 'cursor' && layer === 'global') {
    yield* copyCursorToOldStores(Boolean(action.payload.value));
  }
}

/**
 * Sends every queued change in one request. On success the account's copy is
 * recorded; on failure the changes stay queued for the next change or sign-in.
 */
export function* saveAccountSettings(): Generator<unknown, void, unknown> {
  if (!isOn() || !accountStoreAvailable) return;
  const pending = (yield select(selectPendingSettingWrites)) as SettingCoordinateValue[];
  if (pending.length === 0) return;
  try {
    yield call(axios.put, ACCOUNT_SETTINGS_URL, toAccountSettingsPayload(pending));
    yield put(accountSettingsConfirmed({ writes: pending }));
  } catch (error) {
    if (isNotFound(error)) {
      accountStoreAvailable = false;
      return;
    }
    Logger.warn('Account settings: save failed; kept on this device for the next try', error);
  }
}

// Worked out on the first action, not when this module loads, so loading it
// never calls an action creator (some suites mock the action modules).
let watchedTypes: ReadonlySet<string> | undefined;

const isWatchedSettingAction = (action: AnyAction): boolean => {
  watchedTypes =
    watchedTypes ?? new Set(watchedSettingActions().map(({ actionType }) => actionType));
  return watchedTypes.has(action.type);
};

export default [
  takeEvery(isWatchedSettingAction, onBoundSettingAction),
  takeEvery(settingLayerValueSet.type, onSettingLayerValueSet),
  takeEvery(actionTypes.SET_SIGHT_READING_CURSOR_VISIBLE, onSightReadingCursorToggled),
  debounce(ACCOUNT_SETTINGS_SAVE_DELAY_MS, accountSettingsQueued.type, saveAccountSettings),
];
